import { useCallback, useEffect, useState } from 'react'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classNames from 'classnames'
import { useSearchParams } from 'next/navigation'
import { isMobile } from 'react-device-detect'
import { When } from 'react-if'
import { Button, ButtonVariant, LinkButton } from '@/atoms/Button'
import { ArrowRightIcon } from '@/atoms/Icons/ArrowRightIcon'
import { CheckBoxBlankIcon } from '@/atoms/Icons/CheckBoxBlankIcon'
import { CheckBoxIcon } from '@/atoms/Icons/CheckBoxIcon'
import { CircleCloseIcon } from '@/atoms/Icons/CircleCloseIcon'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { UploadIcon } from '@/atoms/Icons/UploadIcon'
import { Image } from '@/atoms/Image'
import { LazyHydrate } from '@/atoms/LazyHydrate'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import {
  AsH3,
  CaptionSM,
  CaptionXS,
  HeadingLG,
  HeadingXL,
  LabelLG,
  LabelSM,
  ParagraphLG,
  ParagraphMD,
  TitleLG,
  TitleMD,
} from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Modal } from '@/molecules/Modal'
import { useToast } from '@/molecules/Toast'
import { FaqSection } from '@/organisms/FaqSection'
import {
  formatDimensions,
  formatNonTitleLikeLandscapeImagePath,
  formatNonTitleLikeVerticalImagePath,
  formatTitleLogo,
} from '@/services/ContentCatalog'
import { options } from '@/services/ContentfulService'
import { AmbassadorPage, ProjectTheme } from '@/types/codegen-contentful'
import { CatalogMovie } from '@/types/generated-content-catalog-types'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { getBasePath } from '@/utils/sitemap/base'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { TheatricalPifShareSection } from '../PayItForward/TheatricalPayItForwardView/sections/TheatricalPifShareSection'
import { EmailCapture } from '../SignUpView/EmailCapture'
import { useWatchTheatricalContext } from '../WatchTheatricalView/WatchTheatricalContext'
import { AmbassadorActionButton } from './AmbassadorActionButton'
import { AmbassadorVideoCard } from './AmbassadorVideoCard'

const EMAIL_SUBSCRIPTION_GROUP_ID = '8076f720-2070-4f56-ae01-425e75f4351c'

interface Ambassador {
  ambassadorTitle?: string
  shareDescription?: string
  onClick?: () => void
  buttonText?: string
  buttonVariant?: ButtonVariant
  buttonIcon?: React.ReactNode
  videoUrl: string
  thumbnailUrl?: string
  id?: string
  sys: {
    id: string
  }
}
interface AmbassadorViewProps {
  catalogTitle: CatalogMovie | null
  ambassadorPageData: AmbassadorPage
  contentfulProjectTheme?: ProjectTheme | null
}

export const AmbassadorView: React.FC<AmbassadorViewProps> = ({
  catalogTitle,
  ambassadorPageData,
  contentfulProjectTheme,
}) => {
  const { t } = useTranslate('ambassador')
  const { replace, asPath } = useSafeRouter()
  const searchParams = useSearchParams()
  const { locale } = useLocale()
  const track = useSafeTrack()
  const toast = useToast()
  const [success, setSuccess] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [userProvidedConsent, setUserProvidedConsent] = useState(false)
  const [showMoreVideos, setShowMoreVideos] = useState(false)
  const [currentAmbassadorVideo, setCurrentAmbassadorVideo] = useState<Ambassador | null>(null)
  const { name, slug, filmDetails } = useWatchTheatricalContext()
  const { heroBackgroundVideo } = filmDetails || {}
  const {
    uploadModalBackgroundImageUrl,
    uploadTitleMessage,
    uploadSubtitle,
    uploadDeeplink,
    uploadQrCodeImageUrl,
    ctaSectionTitle,
    ctaSectionSubtitle,
    heroTitle,
    heroSubtitle,
    emailSubscriptionGroupId,
    reviewModalBackgroundImageUrl,
    emailCaptureBackgroundImageUrl,
    emailCaptureTitle,
    emailCaptureText,
    ambassadorVideosCollection,
    ambassadorCtAsCollection,
    shareOverrideUrl,
    hideHero,
    emailSuccessButtonText,
    emailSuccessButtonLink,
    emailSuccessMessage,
  } = ambassadorPageData
  const heroBgImagePath = catalogTitle && formatNonTitleLikeLandscapeImagePath(catalogTitle)
  const mobileHeroBgImagePath = catalogTitle && formatNonTitleLikeVerticalImagePath(catalogTitle)
  const titleLogo = formatTitleLogo(catalogTitle)
  const projectName = name as string
  const url = `${getBasePath(locale)}${paths.ambassadors.index}/${slug}`
  const linkCopied = t('linkCopied', 'Link copied.')
  const showAllVideos = (ambassadorVideosCollection && ambassadorVideosCollection?.items.length <= 6) || showMoreVideos
  const ambassadorVideos = showAllVideos
    ? (ambassadorVideosCollection?.items as Ambassador[])
    : (ambassadorVideosCollection?.items.slice(0, 6) as Ambassador[])

  const onVideoCopied = useCallback(
    (videoId: string) => {
      const link = `${url}?video=${videoId}`
      navigator.clipboard.writeText(link)
      toast?.showToast(linkCopied, { icon: <CopyIcon size={24} color="core-gray-200" /> })
    },
    [linkCopied, toast, url],
  )

  useEffect(() => {
    const hash = (asPath as string).split('#')[1] || ''
    Boolean(hash?.includes('browse-angel-originals')) && localStorage.setItem('ambassadorSignup', JSON.stringify(true))
    setSuccess(Boolean(hash?.includes('success')))
    setShowModal(Boolean(hash?.includes('browse-angel-originals')))
  }, [asPath])

  const handleReviewOpen = () => {
    setShowReviewModal(true)
  }

  const handleReviewClose = () => {
    setShowReviewModal(false)
  }

  const handleUploadMessage = () => {
    track('Ambassador Upload Clicked')
    setShowUploadModal(true)
  }

  const handleUploadClose = () => {
    setShowUploadModal(false)
  }

  const handleUserConsent = () => {
    !userProvidedConsent && track('Ambassador Upload Consent Clicked')
    setUserProvidedConsent(!userProvidedConsent)
  }

  const handleAmbassadorVideoClose = () => {
    setCurrentAmbassadorVideo(null)
    if (searchParams.has('video')) {
      const searchParamsToUpdate = new URLSearchParams(searchParams.toString())
      searchParamsToUpdate.delete('video')
      replace(url)
    }
  }

  useEffect(() => {
    const currentVideo =
      searchParams.has('video') &&
      ambassadorVideosCollection?.items.find((ambassador) => {
        return ambassador?.sys?.id === searchParams.get('video')
      })
    if (currentVideo) {
      setCurrentAmbassadorVideo(currentVideo as Ambassador)
      track('Shared Ambassador Link Opened', {
        guid: currentVideo.sys.id,
      })
    }
  }, [searchParams, replace, track, ambassadorVideosCollection?.items])

  return (
    <>
      {!hideHero && (
        <section className="h-screen w-screen bg-cover text-white">
          <div className="relative left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover">
            <When condition={heroBackgroundVideo}>
              <video
                className="relative left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover"
                autoPlay
                playsInline
                muted
                loop
                src={heroBackgroundVideo as string}
              />
            </When>
            <When condition={heroBgImagePath && !heroBackgroundVideo}>
              <div className="relative left-1/2 top-1/2 h-full min-w-full -translate-x-1/2 -translate-y-1/2 bg-cover object-cover">
                <ResponsiveBackgroundImage
                  src={mobileHeroBgImagePath ? getCloudinaryImageUrl({ path: mobileHeroBgImagePath }) : ''}
                  className="!block lg:!hidden"
                />
                <ResponsiveBackgroundImage
                  src={heroBgImagePath ? getCloudinaryImageUrl({ path: heroBgImagePath }) : ''}
                  className="!hidden lg:!block"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-core-gray-950" />
              </div>
            </When>
            <div className="absolute inset-0 bg-gradient-to-tl from-core-gray-950 to-transparent" />
          </div>
          <div className="absolute bottom-[40px] left-1/2 flex w-full -translate-x-1/2 flex-col items-center justify-center gap-6 px-8 text-center">
            {titleLogo && (
              <div className="max-w-full py-[25px] md:max-w-[500px] md:p-0">
                <Image
                  alt={projectName}
                  src={getCloudinaryImageUrl({
                    path: titleLogo.cloudinaryPath,
                    transforms: 'e_trim',
                    ...formatDimensions([500, 'auto'], titleLogo),
                  })}
                  {...formatDimensions([500, 'auto'], titleLogo)}
                />
              </div>
            )}
            <div>
              <TitleLG weight="semibold" className="photon-title-sm md:photon-title-lg max-w-[639px]">
                {heroTitle}
              </TitleLG>
              {heroSubtitle && (
                <TitleLG weight="semibold" className="photon-title-sm md:photon-title-lg max-w-[639px]">
                  {heroSubtitle}
                </TitleLG>
              )}
            </div>
            <EmailCapture
              position="section-1"
              inputClassName="border border-solid border-core-gray-200 !rounded-lg"
              buttonClassName="absolute right-2 max-h-[40px] max-w-[40px] top-[9px] !rounded-md !p-1"
              buttonVariant="black"
              placeholder={t('enterYourEmail', 'Enter your email')}
              subscriptionGroupId={emailSubscriptionGroupId ?? EMAIL_SUBSCRIPTION_GROUP_ID}
              projectSlug={slug}
              className="relative w-full rounded md:w-fit lg:min-w-[352px]"
              projectName={projectName}
              destination={'#browse-angel-originals'}
              shouldShowSuccess={success}
              shouldShowModal={showModal}
              buttonText={' '}
              buttonIcon={<ArrowRightIcon size={28} color="white" />}
              buttonIconClassName="max-h-[28px]"
              hideFormTagLine
              customTrackPayload={{
                location: 'ambassador-hero-cta',
              }}
              successButtonText={emailSuccessButtonText ?? null}
              successButtonLink={emailSuccessButtonLink ?? null}
              successMessage={emailSuccessMessage ?? null}
            />
            <a href="#how-can-you-help">
              <LabelSM className="cursor-pointer">{t('learnMore', 'Learn More')}</LabelSM>
            </a>
          </div>
        </section>
      )}
      <section className="bg-core-gray-950"></section>
      <section
        id="how-can-you-help"
        className="flex flex-col items-center gap-4 bg-black px-8 py-40 text-center text-white"
      >
        {ctaSectionTitle && (
          <HeadingXL weight="bold" className="photon-heading-xs md:photon-heading-xl max-w-[630px] text-pretty">
            {ctaSectionTitle}
          </HeadingXL>
        )}
        <ParagraphLG className="!weight-[350] max-w-[630px] text-pretty">
          {ctaSectionSubtitle ? (
            ctaSectionSubtitle
          ) : (
            <Translate i18nKey="checkOutSharev2" t={t}>
              Check out how you can help share <i>{{ projectName }}&apos;s</i> powerful story below
            </Translate>
          )}
        </ParagraphLG>
        <div className="flex w-full flex-col gap-8 py-8 md:grid md:grid-cols-2 lg:max-w-[1400px] lg:grid-cols-3 lg:px-10 2xl:max-w-[1600px]">
          {ambassadorCtAsCollection?.items?.map((action) => {
            return (
              <AmbassadorActionButton
                key={action?.sys.id}
                href={action?.href}
                heading={action?.heading ?? ''}
                backgroundImageUrl={action?.backgroundImageUrl ?? ''}
                onClick={action?.href ? noop : handleReviewOpen}
                openingBehavior={action?.openingBehavior ?? 'New Tab'}
              />
            )
          })}
        </div>
      </section>
      <LazyHydrate whenVisible>
        <section
          className={classNames(
            'relative flex h-[160vh] w-screen flex-row items-end text-white md:h-[50vh] lg:h-[90vh] xl:h-[100vh]',
            {
              'h-[160vh] se:h-[200vh] md:h-[50vh] lg:h-[90vh] xl:h-[100vh]': emailCaptureText?.json,
            },
          )}
        >
          <div className="relative h-full min-w-full">
            {emailCaptureBackgroundImageUrl && (
              <ResponsiveBackgroundImage
                src={getCloudinaryImageUrl({
                  path: emailCaptureBackgroundImageUrl,
                })}
                backgroundSize="bg-cover"
                className="h-full w-screen bg-top md:bg-center"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-tl from-core-gray-950 to-transparent" />
          </div>
          <div
            className={classNames(
              'absolute inset-y-1/2 bottom-0 top-auto flex h-fit -translate-y-1/2 flex-col items-center gap-8 px-8 md:right-8 md:top-1/2 md:px-0 lg:right-20',
              { 'se:top-1/2 top-1/2': emailCaptureText?.json },
            )}
          >
            <div className="max-w-[325px] p-[25px] md:p-0">
              {titleLogo && (
                <Image
                  alt={projectName}
                  src={getCloudinaryImageUrl({
                    path: titleLogo.cloudinaryPath,
                    transforms: 'e_trim',
                    ...formatDimensions([500, 'auto'], titleLogo),
                  })}
                  {...formatDimensions([500, 'auto'], titleLogo)}
                />
              )}
            </div>
            <div
              className={classNames('flex flex-col items-center', {
                'text-left': emailCaptureTitle && emailCaptureText?.json,
                'text-center': !emailCaptureTitle && !emailCaptureText?.json,
              })}
            >
              {(heroTitle || emailCaptureTitle) && (
                <HeadingXL weight="bold" className="photon-heading-xs md:photon-heading-xl max-w-[500px]">
                  {emailCaptureTitle ? emailCaptureTitle : `${heroTitle}!`}
                </HeadingXL>
              )}
              {emailCaptureText?.json ? (
                <span className="!weight-[350] max-w-[370px] lg:max-w-[487px]">
                  {documentToReactComponents(emailCaptureText?.json, options)}
                </span>
              ) : (
                <ParagraphLG className="!weight-[350] max-w-[370px] lg:max-w-[487px]">
                  {t(
                    'joinAmbassadorNetwork',
                    'Join our Ambassador network to earn special perks on merchandise and tickets.',
                  )}
                </ParagraphLG>
              )}
            </div>
            <EmailCapture
              position="section-1"
              inputClassName="border border-solid border-core-gray-200 !rounded-lg"
              buttonClassName="absolute right-2 max-h-[40px] max-w-[40px] top-[9px] !rounded-md !p-1"
              buttonVariant="black"
              placeholder={t('enterYourEmail', 'Enter your email')}
              subscriptionGroupId={emailSubscriptionGroupId ?? EMAIL_SUBSCRIPTION_GROUP_ID}
              projectSlug={slug}
              className="relative w-full rounded md:w-fit lg:min-w-[352px]"
              projectName={projectName}
              destination={'#browse-angel-originals'}
              shouldShowSuccess={success}
              shouldShowModal={showModal}
              buttonText={' '}
              buttonIcon={<ArrowRightIcon size={28} color="white" />}
              buttonIconClassName="max-h-[28px]"
              hideFormTagLine
              customTrackPayload={{
                location: 'ambassador-bottom-cta',
              }}
              successButtonText={emailSuccessButtonText ?? null}
              successButtonLink={emailSuccessButtonLink ?? null}
              successMessage={emailSuccessMessage ?? null}
            />
          </div>
        </section>
      </LazyHydrate>
      <section aria-labelledby="share-section-title" className="overflow-hidden bg-black py-28" id="share-section">
        <LazyHydrate whenVisible>
          <TheatricalPifShareSection
            projectName={projectName}
            theatricalSlug={slug}
            shareUrl={shareOverrideUrl ?? asPath}
          />
        </LazyHydrate>
      </section>
      <section className="flex flex-col items-center gap-4 overflow-x-hidden bg-core-gray-950 py-20 text-center text-white">
        {uploadTitleMessage && (
          <HeadingXL weight="bold" className="photon-heading-xs md:photon-heading-xl max-w-[500px]">
            {uploadTitleMessage}
          </HeadingXL>
        )}
        <ParagraphLG className="!weight-[350]">
          {uploadSubtitle ? (
            uploadSubtitle
          ) : (
            <Translate i18nKey="seeWhatPeopleAreSaying" t={t}>
              See what people are saying about <i className="mr-1">{{ projectName }}</i>!
            </Translate>
          )}
        </ParagraphLG>
        <Button onClick={handleUploadMessage} variant="white" className="my-6 text-core-gray-950">
          <UploadIcon size={28} color="core-gray-950" />
          <LabelLG>{t('uploadMessage', 'Upload Your Message')}</LabelLG>
        </Button>
        <div className="flex w-full flex-col gap-x-4 gap-y-20 px-10 py-8 md:grid md:grid-cols-2 lg:max-w-[1400px] lg:grid-cols-3 2xl:max-w-[1600px]">
          {ambassadorVideos?.map((ambassador, index) => {
            return (
              <AmbassadorVideoCard
                key={ambassador.sys.id + index}
                {...ambassador}
                onVideoCopied={onVideoCopied}
                showButton={true}
              />
            )
          })}
        </div>
        {!showAllVideos && (
          <Button
            onClick={() => setShowMoreVideos(true)}
            variant="core-gray-600"
            className="mt-10 border-0 px-7 py-[17px] !text-white"
          >
            <LabelLG className="leading-7">{t('viewMoreVideos', 'View More Videos')}</LabelLG>
          </Button>
        )}
      </section>
      <When condition={contentfulProjectTheme?.partnershipKitUrl}>
        <section className="mx-auto grid justify-center bg-black text-white md:flex md:pt-20">
          <div className="flex max-w-[510px] flex-col items-center justify-between gap-y-1 bg-core-gray-900 px-10 py-16 text-center lg:px-20">
            <TitleMD as={AsH3} weight="bold" className="mb-1 text-center">
              {t('partnershipKit', 'Partnership Kit')}
            </TitleMD>
            <CaptionXS className="mb-3 text-base lg:max-w-[350px]" color="core-gray-300">
              {t(
                'partnershipKitDescriptionWithName',
                'Our Partnership kit has resources designed to provide social influencers all the necessary materials to help promote {{title}}.',
                { title: projectName },
              )}
            </CaptionXS>
            <LinkButton
              variant="plain"
              href={contentfulProjectTheme?.partnershipKitUrl ?? ''}
              className="whitespace-nowrap"
            >
              {t('viewPartnershipKit', 'View Partnership Kit')}
            </LinkButton>
          </div>
        </section>
      </When>
      <section key="faqs" className="bg-black py-40">
        <div id="faqs" className="mx-auto max-w-[860px] scroll-mt-[73px] px-8 text-white">
          <LazyHydrate whenVisible>
            <FaqSection />
          </LazyHydrate>
        </div>
      </section>
      <Modal
        className="z-[50]"
        panelClasses="!min-h-[84vh] !md:min-h-[78vh] !min-w-[90vw] xl:!min-w-[1312px] xl:!min-h-[70vh] relative bg-transparent"
        isOpen={showReviewModal}
        onClose={handleReviewClose}
        childWrapperClassName="h-full w-full"
      >
        {reviewModalBackgroundImageUrl && (
          <ResponsiveBackgroundImage
            className="bg-top"
            src={getCloudinaryImageUrl({
              path: reviewModalBackgroundImageUrl,
            })}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-tl from-core-gray-950/80 to-transparent" />
        <div
          className="absolute right-6 top-6 z-[5000] cursor-pointer rounded-full bg-black"
          onClick={handleReviewClose}
        >
          <CircleCloseIcon color="white" size={40} className="cursor-pointer" />
        </div>
        <div className="absolute left-0 top-4 h-full w-full sm:top-12 md:top-0">
          <div className="flex h-full w-full flex-col items-center justify-center px-8 text-center text-white md:items-end lg:px-10">
            <div className="flex flex-col items-center gap-8 pt-40 md:pt-0">
              <div className="flex flex-col items-center">
                <HeadingLG
                  weight="bold"
                  className="photon-heading-xs md:photon-heading-md lg:photon-heading-lg md:mad-w-[443px] mb-4 max-w-[455px] lg:max-w-[455px]"
                >
                  {t('leaveReviewInspire', 'Leave a Review to Inspire')}
                </HeadingLG>
                <ParagraphMD className="!weight-[350] photon-paragraph-sm lg:photon-paragraph-md max-w-[325px] text-core-gray-300 md:max-w-[380px]">
                  <Translate i18nKey="shareReviewInspireRTv2" t={t}>
                    Share your review on Rotten Tomatoes and IMDB. Click on the links below and search{' '}
                    <i>{{ projectName }}</i> to leave your review.
                  </Translate>
                </ParagraphMD>
              </div>
              <LinkButton
                href="https://www.rottentomatoes.com/"
                target="_blank"
                onClick={handleReviewClose}
                variant="white"
                className="px-7 py-[17px] text-core-gray-950"
              >
                <LabelLG>{t('reviewRT', 'Review on Rotten Tomatoes')}</LabelLG>
              </LinkButton>
              <LinkButton
                href="https://www.imdb.com/"
                target="_blank"
                onClick={handleReviewClose}
                variant="white"
                className="px-7 py-[17px] text-core-gray-950"
              >
                <LabelLG>{t('reviewIMDB', 'Review on IMDB')}</LabelLG>
              </LinkButton>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        className="z-[50]"
        panelClasses="!min-h-[84vh] !md:min-h-[78vh] !min-w-[90vw] xl:!min-w-[1312px] xl:!min-h-[70vh] relative bg-transparent"
        isOpen={showUploadModal}
        onClose={handleUploadClose}
        childWrapperClassName="h-full w-full"
      >
        {uploadModalBackgroundImageUrl && (
          <ResponsiveBackgroundImage
            className="bg-top"
            src={getCloudinaryImageUrl({
              path: uploadModalBackgroundImageUrl,
            })}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-tl from-core-gray-950/80 to-transparent" />
        <div
          className="absolute right-6 top-6 z-[5000] cursor-pointer rounded-full bg-black"
          onClick={handleUploadClose}
        >
          <CircleCloseIcon color="white" size={40} className="cursor-pointer" />
        </div>
        <div className="absolute left-0 top-4 h-full w-full sm:top-12 md:top-0">
          <div className="flex h-full w-full flex-col items-center justify-center px-4 text-center text-white md:items-end lg:px-10">
            <div className="flex flex-col items-center gap-8 pt-40 md:pt-0">
              <div className="flex flex-col items-center">
                <HeadingLG
                  weight="bold"
                  className="photon-heading-xs md:photon-heading-md lg:photon-heading-lg md:mad-w-[443px] mb-4 max-w-[455px] lg:max-w-[455px]"
                >
                  {t('spreadMessageHopeCourage', 'Spread Your Message of Hope & Courage')}
                </HeadingLG>
                <ParagraphMD className="!weight-[350] photon-paragraph-sm lg:photon-paragraph-md max-w-[347px] text-core-gray-300 md:max-w-[380px]">
                  <Translate i18nKey="shareReviewInspireSubmitv2" t={t}>
                    We&apos;re excited to hear how the <i>{{ projectName }}</i> film has impacted you, your family, or
                    your community. Submit your story on the Angel App.
                  </Translate>
                </ParagraphMD>
              </div>
              {uploadDeeplink && (
                <LinkButton
                  href={uploadDeeplink}
                  target="_blank"
                  disabled={!userProvidedConsent}
                  onClick={handleUploadClose}
                  variant="white"
                  className={classNames('px-7 py-[17px] text-core-gray-950', {
                    hidden: !isMobile,
                  })}
                >
                  <LabelLG>{t('openAngelApp', 'Open Angel App')}</LabelLG>
                </LinkButton>
              )}
              {uploadQrCodeImageUrl && (
                <Image
                  alt={projectName}
                  src={uploadQrCodeImageUrl}
                  height={150}
                  width={150}
                  className={classNames('', {
                    hidden: isMobile || !userProvidedConsent,
                  })}
                />
              )}
              <div className="flex flex-row gap-4 text-left">
                <div onClick={handleUserConsent} className="w-10 cursor-pointer">
                  <When condition={userProvidedConsent}>
                    <CheckBoxIcon color={'white'} size={40} />
                  </When>
                  <When condition={!userProvidedConsent}>
                    <CheckBoxBlankIcon color={'white'} size={40} />
                  </When>
                </div>
                <CaptionSM className="photon-footer md:photon-caption-sm !weight-[350] max-w-[217px] text-core-gray-300 md:max-w-[328px]">
                  {t(
                    'uploadingVideoMessageConsentAngel',
                    'Uploading a video or message. I consent to have my image, likeness, first name and video testimonial used by Angel for marketing and promotional purposes.',
                  )}
                </CaptionSM>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        className=""
        panelClasses="!bg-black"
        isOpen={Boolean(currentAmbassadorVideo)}
        onClose={handleAmbassadorVideoClose}
        childWrapperClassName="h-full w-full"
      >
        <div
          className="absolute right-6 top-6 z-[5000] cursor-pointer rounded-full bg-black"
          onClick={handleAmbassadorVideoClose}
        >
          <CircleCloseIcon color="white" size={40} className="cursor-pointer" />
        </div>
        <div className="px-4 pb-4 pt-12">
          <AmbassadorVideoCard {...currentAmbassadorVideo} onVideoCopied={onVideoCopied} showButton={false} />
        </div>
      </Modal>
    </>
  )
}

const noop = () => {}

// We are hiding this modal for now as it is more intrusive, but Ryan wanted to keep it just in case we need it in the future.

/* <Modal
className="z-[50]"
panelClasses="!min-h-[84vh] !md:min-h-[78vh] !min-w-[90vw] xl:!min-w-[1312px] xl:!min-h-[70vh] relative bg-transparent"
isOpen={showAmbassadorModal}
onClose={handleAmbassadorClose}
childWrapperClassName="h-full w-full"
>
<ResponsiveBackgroundImage
  className="bg-top"
  src={getCloudinaryImageUrl({
    path: '/v1705336283/angel-app/Cabrini/Ambassadors/3831099e5b72e26aac33676678e442a8.png',
  })}
  srcUrls={{
    xs: 'https://images.angelstudios.com/image/upload/v1705348928/angel-app/Cabrini/Ambassadors/CAB_63864_ambassador1.png',
    sm: 'https://images.angelstudios.com/image/upload/v1705348928/angel-app/Cabrini/Ambassadors/CAB_63864_ambassador1.png',
    md: '/v1705336283/angel-app/Cabrini/Ambassadors/3831099e5b72e26aac33676678e442a8.png',
    lg: '/v1705336283/angel-app/Cabrini/Ambassadors/3831099e5b72e26aac33676678e442a8.png',
    xl: '/v1705336283/angel-app/Cabrini/Ambassadors/3831099e5b72e26aac33676678e442a8.png',
    '2xl': '/v1705336283/angel-app/Cabrini/Ambassadors/3831099e5b72e26aac33676678e442a8.png',
  }}
/>
<div className="absolute inset-0 bg-gradient-to-tl from-core-gray-950/80 to-transparent" />
<div
  className="absolute right-6 top-6 z-[5000] cursor-pointer rounded-full bg-black"
  onClick={handleAmbassadorClose}
>
  <CircleCloseIcon color="white" size={40} className="cursor-pointer" />
</div>
<div className="absolute left-0 top-0 h-full w-full">
  <div className="flex h-full w-full flex-col items-center justify-center px-8 text-center text-white md:items-end md:text-left lg:px-10">
    <div className="flex flex-col items-center gap-8 pt-40 md:items-start md:pt-0">
      <div className="max-w-full py-[25px] md:max-w-[276px] md:p-0 lg:max-w-[400px]">
        <Image
          alt={projectName}
          src={contentfulLogoCloudinaryPath}
          height={logo?.[0]?.height}
          width={logo?.[0]?.width}
        />
      </div>
      <div className="flex flex-col items-center md:items-start">
        <HeadingLG
          weight="bold"
          className="photon-title-lg md:photon-heading-md lg:photon-heading-lg md:mad-w-[443px] mb-4 max-w-[284px] lg:max-w-[455px]"
        >
          {t('becomeAmbassadorCourage', 'Become an Ambassador of Courage')}!
        </HeadingLG>
        <ParagraphMD className="!weight-[350] photon-paragraph-sm lg:photon-paragraph-md max-w-[325px] text-core-gray-300 md:max-w-[380px]">
          {t(
            'joinAmbassadorMerchTickets',
            'Join our Ambassador network to earn special perks on merchandise and tickets.',
          )}
        </ParagraphMD>
      </div>
      <EmailCapture
        position="ambassador-modal"
        inputClassName="border border-solid border-core-gray-200 !rounded-lg"
        buttonClassName="absolute right-2 max-h-[40px] max-w-[40px] top-[9px] !rounded-md !p-1"
        buttonVariant="black"
        placeholder={t('enterYourEmail', 'Enter your email')}
        subscriptionGroupId={EMAIL_SUBSCRIPTION_GROUP_ID}
        projectSlug={slug}
      className="relative w-full rounded md:w-fit lg:min-w-[352px]"
        projectName={projectName}
        destination={'#browse-angel-originals'}
        shouldShowSuccess={success}
        shouldShowModal={showModal}
        buttonText={' '}
        buttonIcon={<ArrowRightIcon size={28} color="white" />}
        buttonIconClassName="max-h-[28px]"
        hideFormTagLine
        customTrackPayload={{
          location: 'ambassador-modal-cta',
        }}
      />
    </div>
  </div>
</div>
</Modal> */
